import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { boardApi, cartApi, productApi, reviewApi } from '@/api/endpoints';
import { useAuthStore } from '@/store/authStore';
import { Alert, Empty, ErrorState, Loading } from '@/components/common/State';
import Avatar from '@/components/common/Avatar';
import MediaAttach, { MediaThumbs } from '@/components/common/MediaAttach';
import { useMediaUpload } from '@/components/common/useMediaUpload';
import type { MediaType } from '@/api/types';
import { toMediaInputs, type DraftMedia } from '@/utils/media';
import { formatDate, formatWon, stars } from '@/utils/format';

type TabKey = 'description' | 'ingredients' | 'reviews' | 'qna';

interface GalleryItem {
  mediaType: MediaType;
  url: string;
  thumbnailUrl?: string;
}

export default function ProductDetailPage() {
  const { productId } = useParams();
  const id = Number(productId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  const [pickedOptionId, setPickedOptionId] = useState<number | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [mainImage, setMainImage] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>('description');
  const [message, setMessage] = useState<string | null>(null);
  const [optionWarning, setOptionWarning] = useState(false);
  const [optionBlinking, setOptionBlinking] = useState(false);
  const optionSelectRef = useRef<HTMLSelectElement>(null);

  const { data: product, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['product', id],
    queryFn: () => productApi.detail(id),
    enabled: Number.isFinite(id),
  });

  const reviews = useQuery({
    queryKey: ['reviews', id],
    queryFn: () => reviewApi.list({ productId: id, size: 10 }),
    enabled: tab === 'reviews' && Number.isFinite(id),
  });

  const reviewStats = useQuery({
    queryKey: ['reviewStats', id],
    queryFn: () => reviewApi.stats(id),
    enabled: Number.isFinite(id),
  });

  const qna = useQuery({
    queryKey: ['qna', id],
    queryFn: () => boardApi.list({ boardType: 'QNA', productId: id, size: 10 }),
    enabled: tab === 'qna' && Number.isFinite(id),
  });

  const activeOptions = useMemo(
    () => product?.options.filter((option) => option.active) ?? [],
    [product],
  );
  // 살 수 있는 옵션이 하나뿐이면 고를 필요가 없으므로 사용자가 고르기 전까지 그 옵션으로 둔다.
  const purchasableOptions = activeOptions.filter((option) => !option.soldOut);
  const selectedOptionId =
    pickedOptionId ?? (purchasableOptions.length === 1 ? purchasableOptions[0].optionId : null);
  const selectedOption = activeOptions.find((o) => o.optionId === selectedOptionId) ?? null;

  const addToCart = useMutation({
    mutationFn: () =>
      cartApi.add({ productId: id, optionId: selectedOptionId!, quantity }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cart'] });
      setMessage('장바구니에 담았습니다.');
    },
    onError: (e: Error) => setMessage(e.message),
  });

  const unitPrice = product
    ? product.sellingPrice + (selectedOption?.additionalPrice ?? 0)
    : 0;

  const requireLogin = () => {
    if (!user) {
      navigate(`/login?redirect=/products/${id}`);
      return true;
    }
    return false;
  };

  // 옵션 없이 담기/구매를 누르면 경고를 띄우고 옵션 선택창을 두 번 깜박여 눈길을 끈다.
  const warnOptionRequired = () => {
    setMessage(null);
    setOptionWarning(true);
    // 연달아 눌러도 다시 깜박이도록 애니메이션 클래스를 뗐다가 다음 프레임에 붙인다.
    setOptionBlinking(false);
    requestAnimationFrame(() => setOptionBlinking(true));
    const select = optionSelectRef.current;
    select?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    select?.focus({ preventScroll: true });
  };

  const handleAddToCart = () => {
    if (requireLogin()) return;
    if (!selectedOptionId) {
      warnOptionRequired();
      return;
    }
    addToCart.mutate();
  };

  const handleBuyNow = () => {
    if (requireLogin()) return;
    if (!selectedOptionId) {
      warnOptionRequired();
      return;
    }
    navigate('/checkout', {
      state: {
        lines: [{ productId: id, optionId: selectedOptionId, quantity }],
      },
    });
  };

  if (isLoading) return <Loading />;
  if (isError) return <ErrorState error={error} onRetry={refetch} />;
  if (!product) return <Empty title="상품을 찾을 수 없습니다." />;

  // 대표 이미지 + 상세 미디어(이미지·동영상)를 한 줄로 보여 준다.
  const gallery: GalleryItem[] = [
    ...(product.thumbnailUrl ? [{ mediaType: 'IMAGE' as const, url: product.thumbnailUrl }] : []),
    ...product.media.map((m) => ({ mediaType: m.mediaType, url: m.url, thumbnailUrl: m.thumbnailUrl })),
  ];
  const current = gallery.find((item) => item.url === mainImage) ?? gallery[0];

  return (
    <>
      <div className="detail-layout">
        <div>
          {current?.mediaType === 'VIDEO' ? (
            <video
              key={current.url}
              className="detail-gallery__main"
              src={current.url}
              poster={current.thumbnailUrl}
              controls
              playsInline
              preload="metadata"
            />
          ) : (
            <img className="detail-gallery__main" src={current?.url} alt={product.name} />
          )}
          {gallery.length > 1 && (
            <div className="detail-gallery__thumbs">
              {gallery.map((item) => (
                <button
                  key={item.url}
                  type="button"
                  className={`media-tile${item.url === current?.url ? ' is-active' : ''}`}
                  style={{ width: 64, height: 64 }}
                  onClick={() => setMainImage(item.url)}
                >
                  {item.thumbnailUrl || item.mediaType === 'IMAGE' ? (
                    <img src={item.thumbnailUrl ?? item.url} alt="" />
                  ) : (
                    <span className="media-tile__placeholder">동영상</span>
                  )}
                  {item.mediaType === 'VIDEO' && <span className="media-tile__play" aria-hidden="true" />}
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <span className="muted">
            {product.brand} ·{' '}
            {/* 여러 카테고리에 속할 수 있다. 대표가 맨 앞에 온다. */}
            {product.categories.map((category, index) => (
              <span key={category.categoryId}>
                {index > 0 && ' · '}
                <Link to={`/products?categoryId=${category.categoryId}`} className="muted">
                  {category.name}
                </Link>
              </span>
            ))}
          </span>
          <h1 className="detail-title">{product.name}</h1>
          <p className="muted">{product.summary}</p>

          <div className="price-row" style={{ margin: '18px 0' }}>
            {product.discountRate > 0 && <span className="discount-rate">{product.discountRate}%</span>}
            <span className="price" style={{ fontSize: 26 }}>
              {formatWon(product.sellingPrice)}
            </span>
            {product.discountRate > 0 && <span className="price--strike">{formatWon(product.price)}</span>}
          </div>

          <div className="rating-line" style={{ marginBottom: 16 }}>
            <span className="stars">{stars(Math.round(product.ratingAverage))}</span>{' '}
            {product.ratingAverage.toFixed(1)} · 리뷰 {reviewStats.data?.totalCount ?? product.reviewCount}건
          </div>

          <table className="spec-table">
            <tbody>
              <tr>
                <th>용량</th>
                <td>{product.volume ?? '-'}</td>
              </tr>
              <tr>
                <th>배송비</th>
                <td>50,000원 이상 무료 (미만 3,000원)</td>
              </tr>
              <tr>
                <th>판매상태</th>
                <td>{product.status === 'ON_SALE' ? '판매중' : '품절/판매중지'}</td>
              </tr>
            </tbody>
          </table>

          <div className="field" style={{ marginTop: 20 }}>
            <label htmlFor="option">옵션 선택</label>
            <select
              ref={optionSelectRef}
              id="option"
              className={[
                'select',
                optionWarning && 'select--invalid',
                optionBlinking && 'select--blink',
              ]
                .filter(Boolean)
                .join(' ')}
              value={selectedOptionId ?? ''}
              aria-invalid={optionWarning}
              aria-describedby={optionWarning ? 'option-warning' : undefined}
              onAnimationEnd={() => setOptionBlinking(false)}
              onChange={(e) => {
                setPickedOptionId(e.target.value ? Number(e.target.value) : null);
                if (e.target.value) setOptionWarning(false);
              }}
            >
              <option value="">옵션을 선택하세요</option>
              {activeOptions.map((option) => (
                <option key={option.optionId} value={option.optionId} disabled={option.soldOut}>
                  {option.name}
                  {option.additionalPrice > 0 ? ` (+${formatWon(option.additionalPrice)})` : ''}
                  {option.soldOut ? ' - 품절' : ''}
                </option>
              ))}
            </select>
            {optionWarning && (
              <p id="option-warning" className="option-warning" role="alert">
                <span className="option-warning__icon" aria-hidden>
                  !
                </span>
                옵션을 선택해 주세요.
              </p>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div className="qty-control">
              <button type="button" onClick={() => setQuantity((q) => Math.max(1, q - 1))}>
                −
              </button>
              <span>{quantity}</span>
              <button
                type="button"
                onClick={() =>
                  setQuantity((q) => Math.min(selectedOption?.stockQuantity ?? 99, q + 1))
                }
              >
                ＋
              </button>
            </div>
            <strong style={{ fontSize: 18 }}>{formatWon(unitPrice * quantity)}</strong>
          </div>

          {message && <Alert tone={addToCart.isError ? 'error' : 'info'}>{message}</Alert>}

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              className="btn btn--outline btn--lg btn--block"
              onClick={handleAddToCart}
              disabled={addToCart.isPending || product.status !== 'ON_SALE'}
            >
              장바구니
            </button>
            <button
              type="button"
              className="btn btn--primary btn--lg btn--block"
              onClick={handleBuyNow}
              disabled={product.status !== 'ON_SALE'}
            >
              바로 구매
            </button>
          </div>
        </div>
      </div>

      <div className="section" style={{ marginTop: 48 }}>
        <div className="tabs">
          {(
            [
              ['description', '상세 설명'],
              ['ingredients', '성분'],
              ['reviews', `리뷰 ${reviewStats.data?.totalCount ?? 0}`],
              ['qna', '상품문의'],
            ] as [TabKey, string][]
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={tab === key ? 'is-active' : undefined}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'description' && (
          <div
            className="card"
            style={{ padding: 24 }}
            // 상세 설명은 관리자가 에디터로 작성한 HTML 이다.
            dangerouslySetInnerHTML={{ __html: product.description ?? '<p>준비 중입니다.</p>' }}
          />
        )}

        {tab === 'ingredients' && (
          <div className="card">
            <div className="card__body">
              {product.ingredients.length === 0 ? (
                <Empty title="등록된 성분 정보가 없습니다." />
              ) : (
                <table className="spec-table">
                  <tbody>
                    {product.ingredients.map((ingredient) => (
                      <tr key={ingredient.ingredientId}>
                        <th>{ingredient.name}</th>
                        <td>
                          {ingredient.content && <strong>{ingredient.content} </strong>}
                          {ingredient.description}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {tab === 'reviews' && (
          <div>
            {reviews.isLoading && <Loading />}
            {reviews.data?.content.length === 0 && <Empty title="아직 리뷰가 없습니다." />}
            {reviews.data?.content.map((review) => (
              <div key={review.reviewId} className="card" style={{ marginBottom: 12 }}>
                <div className="card__body">
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div className="writer">
                      <Avatar src={review.writerProfileImageUrl} size={28} />
                      <strong>{review.writer}</strong>
                      <span className="stars">{stars(review.rating)}</span>{' '}
                      {review.verifiedPurchase && <span className="badge badge--success">구매 인증</span>}
                    </div>
                    <span className="muted">{formatDate(review.createdAt)}</span>
                  </div>
                  {review.title && <p style={{ fontWeight: 600, margin: '10px 0 4px' }}>{review.title}</p>}
                  <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{review.content}</p>
                  <MediaThumbs media={review.media} size={80} />
                  <ReviewComments reviewId={review.reviewId} count={review.commentCount} />
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'qna' && (
          <div>
            <div style={{ marginBottom: 12, textAlign: 'right' }}>
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={() => {
                  if (requireLogin()) return;
                  navigate(`/boards/write?boardType=QNA&productId=${id}`);
                }}
              >
                문의하기
              </button>
            </div>
            {qna.isLoading && <Loading />}
            {qna.data?.content.length === 0 && <Empty title="등록된 문의가 없습니다." />}
            <div className="table-wrap">
              {qna.data && qna.data.content.length > 0 && (
                <table className="table">
                  <thead>
                    <tr>
                      <th>제목</th>
                      <th>작성자</th>
                      <th>상태</th>
                      <th>작성일</th>
                    </tr>
                  </thead>
                  <tbody>
                    {qna.data.content.map((post) => (
                      <tr key={post.postId}>
                        <td className="wrap">
                          <a href={`/boards/${post.postId}?boardType=QNA`}>
                            {post.secret && '🔒 '}
                            {post.title}
                          </a>
                        </td>
                        <td>
                          <span className="writer">
                            <Avatar src={post.writerProfileImageUrl} size={20} />
                            {post.writer}
                          </span>
                        </td>
                        <td>{post.status === 'ANSWERED' ? '답변완료' : '접수'}</td>
                        <td>{formatDate(post.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/**
 * 리뷰 댓글. 목록에는 개수만 내려오므로 펼칠 때 본문을 가져온다.
 * 운영자가 쓰면 서버가 공식 답변으로 표시해 준다.
 */
function ReviewComments({ reviewId, count }: { reviewId: number; count: number }) {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState('');
  const [media, setMedia] = useState<DraftMedia[]>([]);
  const [error, setError] = useState<string | null>(null);
  const uploader = useMediaUpload('reviews');

  const comments = useQuery({
    queryKey: ['reviewComments', reviewId],
    queryFn: () => reviewApi.comments(reviewId),
    enabled: open,
  });

  const add = useMutation({
    mutationFn: async () => {
      const uploaded = await uploader.upload(media);
      setMedia(uploaded);
      return reviewApi.addComment(reviewId, { content, media: toMediaInputs(uploaded) });
    },
    onSuccess: () => {
      setContent('');
      setMedia([]);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['reviewComments', reviewId] });
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const remove = useMutation({
    mutationFn: (commentId: number) => reviewApi.removeComment(commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviewComments', reviewId] });
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div style={{ marginTop: 12, borderTop: '1px solid var(--gray-200)', paddingTop: 10 }}>
      <button type="button" className="btn btn--ghost btn--sm" onClick={() => setOpen((v) => !v)}>
        댓글 {count}
        {open ? ' 접기' : ' 보기'}
      </button>

      {open && (
        <div style={{ marginTop: 10 }}>
          {error && <Alert tone="error">{error}</Alert>}
          {comments.isLoading && <Loading />}
          {comments.data?.length === 0 && <p className="muted">첫 댓글을 남겨 보세요.</p>}

          {comments.data?.map((c) => (
            <div
              key={c.commentId}
              style={{
                padding: '8px 0',
                borderLeft: c.answer ? '3px solid var(--brand-500)' : undefined,
                paddingLeft: c.answer ? 10 : 0,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <strong className="writer" style={{ fontSize: 13 }}>
                  <Avatar src={c.writerProfileImageUrl} size={22} />
                  {c.writer} {c.answer && <span className="badge badge--info">공식 답변</span>}
                </strong>
                <span className="muted" style={{ fontSize: 12 }}>
                  {formatDate(c.createdAt)}
                  {user?.userId === c.userId && (
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => remove.mutate(c.commentId)}
                    >
                      삭제
                    </button>
                  )}
                </span>
              </div>
              <p style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap', fontSize: 14 }}>{c.content}</p>
              <MediaThumbs media={c.media} size={70} />
            </div>
          ))}

          {user ? (
            <div style={{ marginTop: 10 }}>
              <textarea
                className="textarea"
                style={{ minHeight: 70 }}
                placeholder="댓글을 입력하세요"
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
              <MediaAttach value={media} onChange={setMedia} max={3} progress={uploader.progress} />
              <div style={{ textAlign: 'right' }}>
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  disabled={!content.trim() || add.isPending}
                  onClick={() => add.mutate()}
                >
                  댓글 등록
                </button>
              </div>
            </div>
          ) : (
            <p className="muted" style={{ fontSize: 13 }}>
              댓글을 쓰려면 로그인하세요.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
